import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { type CareerProfile } from 'CareerProfiles';
import { useGetStudentNetworkProfilesQuery } from '../api/studentNetworkProfilesApi';
import { type StudentNetworkProfileFilters } from '../api/studentNetworkV2Api.types';

const PAGE_SIZE = 20;

export function useStudentProfiles(filters: StudentNetworkProfileFilters = {}) {
    const [offset, setOffset] = useState(0);
    const filtersRef = useRef(filters);
    const accumulatedRef = useRef<CareerProfile[]>([]);

    // Reset offset and accumulated profiles when filters change
    useEffect(() => {
        if (JSON.stringify(filters) !== JSON.stringify(filtersRef.current)) {
            filtersRef.current = filters;
            accumulatedRef.current = [];
            setOffset(0);
        }
    }, [filters]);

    const { data, isFetching, isLoading, isError, error, refetch } = useGetStudentNetworkProfilesQuery({
        filters,
        limit: PAGE_SIZE,
        offset,
        view: 'student_network_profiles',
    });

    // Append new page results to accumulated list, deduplicating by ID
    const profiles = useMemo(() => {
        if (!data?.profiles) return accumulatedRef.current;

        if (offset === 0) {
            accumulatedRef.current = data.profiles;
        } else {
            const existingIds = new Set(accumulatedRef.current.map(p => p.id));
            const newProfiles = data.profiles.filter(p => !existingIds.has(p.id));
            if (newProfiles.length > 0) {
                accumulatedRef.current = [...accumulatedRef.current, ...newProfiles];
            }
        }

        return accumulatedRef.current;
    }, [data, offset]);

    const totalCount = data?.totalCount ?? 0;

    const loadMore = useCallback(() => {
        if (!isFetching && data && offset + PAGE_SIZE < data.totalCount) {
            setOffset(prev => prev + PAGE_SIZE);
        }
    }, [data, offset, isFetching]);

    const hasMore = profiles.length < totalCount;

    // Show spinner when fetching the first page (initial load or filter change)
    const isRefreshing = isFetching && offset === 0;

    return {
        profiles,
        totalCount,
        isLoading: isLoading || isRefreshing,
        isFetching,
        isError,
        error,
        loadMore,
        hasMore,
        retry: refetch,
    };
}
